import { CheckRow } from "@/components/admin/ui/CheckRow";
import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

import { reportFormError } from "@/lib/form-feedback";
import { supabase } from "@/integrations/supabase/client";
import { recordSignIn } from "@/lib/auth-log.functions";
import { signInGuarded } from "@/lib/login-guard.functions";
import { requestMagicLink } from "@/lib/magic-link.functions";
import { requestPasswordReset } from "@/lib/password-reset.functions";
import { isRememberMe, setRememberMe } from "@/lib/remember-me";
import { Field, CONTROL_CLASS, TextField } from "@/components/site/fields";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Locale = "sv" | "en" | "es";

const COPY = {
  sv: {
    title: "Logga in",
    intro: "Inloggningen är till för skolans personal. Anmälan till skolan görs utan konto.",
    email: "E-post",
    emailPlaceholder: "Ange e-post",
    password: "Lösenord",
    passwordPlaceholder: "Ange lösenord",
    submit: "Logga in",
    submitting: "Loggar in…",
    forgot: "Glömt lösenordet?",
    back: "Tillbaka till inloggning",
    resetTitle: "Återställ lösenord",
    resetIntro:
      "Ange din e-postadress så skickar vi en länk där du kan välja ett nytt lösenord.",
    resetSubmit: "Skicka återställningslänk",
    resetSubmitting: "Skickar…",
    resetSent: "Om adressen finns hos oss har vi skickat ett mail med en återställningslänk.",
    show: "Visa lösenord",
    hide: "Dölj lösenord",
    missing: "Fyll i både e-post och lösenord.",
    invalid: "Fel e-post eller lösenord.",
    generic: "Något gick fel. Försök igen om en stund.",
    newTitle: "Välj nytt lösenord",
    newIntro: "Ange ett nytt lösenord för ditt personalkonto.",
    newPassword: "Nytt lösenord",
    newPasswordPlaceholder: "Ange nytt lösenord",
    repeatPassword: "Upprepa lösenord",
    repeatPlaceholder: "Ange lösenordet igen",
    save: "Spara lösenord",
    saving: "Sparar…",
    tooShort: "Lösenordet måste vara minst 8 tecken.",
    mismatch: "Lösenorden matchar inte.",
    saved: "Lösenordet är uppdaterat.",
    linkExpired: "Länken är inte längre giltig. Begär en ny återställningslänk.",
  },
  en: {
    title: "Sign in",
    intro: "Sign-in is for school staff. Applying to the school requires no account.",
    email: "Email",
    emailPlaceholder: "Enter email",
    password: "Password",
    passwordPlaceholder: "Enter password",
    submit: "Sign in",
    submitting: "Signing in…",
    forgot: "Forgot your password?",
    back: "Back to sign in",
    resetTitle: "Reset password",
    resetIntro: "Enter your email address and we will send you a link to choose a new password.",
    resetSubmit: "Send reset link",
    resetSubmitting: "Sending…",
    resetSent: "If the address exists with us, we have sent an email with a reset link.",
    show: "Show password",
    hide: "Hide password",
    missing: "Please fill in both email and password.",
    invalid: "Incorrect email or password.",
    generic: "Something went wrong. Please try again shortly.",
    newTitle: "Choose a new password",
    newIntro: "Set a new password for your staff account.",
    newPassword: "New password",
    newPasswordPlaceholder: "Enter new password",
    repeatPassword: "Repeat password",
    repeatPlaceholder: "Enter the password again",
    save: "Save password",
    saving: "Saving…",
    tooShort: "The password must be at least 8 characters.",
    mismatch: "The passwords do not match.",
    saved: "Your password has been updated.",
    linkExpired: "This link is no longer valid. Please request a new reset link.",
  },
  es: {
    title: "Iniciar sesión",
    intro: "El acceso está destinado al personal del colegio. Para solicitar una plaza no es necesario crear una cuenta.",
    email: "Correo electrónico",
    emailPlaceholder: "Introduce tu correo electrónico",
    password: "Contraseña",
    passwordPlaceholder: "Introduce tu contraseña",
    submit: "Iniciar sesión",
    submitting: "Iniciando sesión…",
    forgot: "¿Has olvidado la contraseña?",
    back: "Volver al inicio de sesión",
    resetTitle: "Restablecer contraseña",
    resetIntro: "Introduce tu correo electrónico y te enviaremos un enlace para elegir una nueva contraseña.",
    resetSubmit: "Enviar enlace",
    resetSubmitting: "Enviando…",
    resetSent: "Si la dirección está registrada, hemos enviado un correo con un enlace para restablecer la contraseña.",
    show: "Mostrar contraseña",
    hide: "Ocultar contraseña",
    missing: "Completa el correo electrónico y la contraseña.",
    invalid: "El correo electrónico o la contraseña son incorrectos.",
    generic: "Ha ocurrido un error. Inténtalo de nuevo dentro de unos instantes.",
    newTitle: "Elige una nueva contraseña",
    newIntro: "Establece una nueva contraseña para tu cuenta de personal.",
    newPassword: "Nueva contraseña",
    newPasswordPlaceholder: "Introduce la nueva contraseña",
    repeatPassword: "Repetir contraseña",
    repeatPlaceholder: "Introduce de nuevo la contraseña",
    save: "Guardar contraseña",
    saving: "Guardando…",
    tooShort: "La contraseña debe tener al menos 8 caracteres.",
    mismatch: "Las contraseñas no coinciden.",
    saved: "La contraseña se ha actualizado.",
    linkExpired: "Este enlace ya no es válido. Solicita un nuevo enlace.",
  },
} as const;

const BLOCKED_COPY: Record<Locale, (wait: string) => string> = {
  sv: (wait) =>
    `För många misslyckade inloggningar. Försök igen om ${wait}. Skolans administratörer har fått en notering om försöken.`,
  en: (wait) =>
    `Too many failed sign-in attempts. Try again in ${wait}. The school's administrators have been notified.`,
  es: (wait) =>
    `Demasiados intentos fallidos. Vuelve a intentarlo en ${wait}. Los administradores del colegio han sido informados.`,
};

const UNIT: Record<Locale, { second: string; minute: string; hour: string }> = {
  sv: { second: "sekunder", minute: "minuter", hour: "timmar" },
  en: { second: "seconds", minute: "minutes", hour: "hours" },
  es: { second: "segundos", minute: "minutos", hour: "horas" },
};

function formatWait(seconds: number, locale: Locale): string {
  const u = UNIT[locale];
  if (seconds < 60) return `${Math.max(seconds, 1)} ${u.second}`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} ${u.minute}`;
  return `${Math.ceil(seconds / 3600)} ${u.hour}`;
}

/** Sidorna där man väljer nytt lösenord, en per språk. */
export const RESET_PATH: Record<Locale, string> = {
  sv: "/aterstall-losenord",
  en: "/en/reset-password",
  es: "/es/restablecer-contrasena",
};

const EXTRA: Record<Locale, {
  remember: string;
  rememberHelp: string;
  magic: string;
  magicSubmit: string;
  magicSending: string;
  magicSent: string;
}> = {
  sv: {
    remember: "Kom ihåg mig",
    rememberHelp: "Håller dig inloggad på den här enheten tills du loggar ut.",
    magic: "Logga in utan lösenord",
    magicSubmit: "Skicka inloggningslänk",
    magicSending: "Skickar…",
    magicSent: "Om adressen finns hos oss har vi skickat en inloggningslänk.",
  },
  en: {
    remember: "Remember me",
    rememberHelp: "Keeps you signed in on this device until you sign out.",
    magic: "Sign in without a password",
    magicSubmit: "Send sign-in link",
    magicSending: "Sending…",
    magicSent: "If the address exists with us, we have sent a sign-in link.",
  },
  es: {
    remember: "Recuérdame",
    rememberHelp: "Mantiene la sesión iniciada en este dispositivo hasta que cierres sesión.",
    magic: "Acceder sin contraseña",
    magicSubmit: "Enviar enlace de acceso",
    magicSending: "Enviando…",
    magicSent: "Si la dirección existe, hemos enviado un enlace de acceso.",
  },
};

const AUTH_PATH: Record<Locale, string> = {
  sv: "/auth",
  en: "/en/auth",
  es: "/es/auth",
};

function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  showLabel,
  hideLabel,
  autoComplete,
  name,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  showLabel: string;
  hideLabel: string;
  autoComplete: string;
  name?: string;
}) {
  const id = React.useId();
  const [visible, setVisible] = React.useState(false);

  return (
    <Field label={label} htmlFor={id} required>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          className={cn(CONTROL_CLASS, "pr-11")}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          {...(name ? { name } : {})}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </Field>
  );
}

function Shell({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mx-auto max-w-md px-5 py-20">
      <div className="rounded-xl border border-border bg-card p-7 shadow-sm">
        <span className="inline-flex h-10 w-10 items-center justify-center text-primary">
          <Lock className="h-4.5 w-4.5" aria-hidden="true" />
        </span>
        <h1 className="mt-4 font-heading text-2xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
        {children}
      </div>
    </section>
  );
}

/** Inloggning för personal, med ögonsymbol och återställningsläge. Ingen registrering. */
export function SignInScreen({ locale, destination = "/admin" }: { locale: Locale; destination?: string }) {
  const t = { ...COPY[locale], ...EXTRA[locale], blocked: BLOCKED_COPY[locale] };
  const navigate = useNavigate();
  const [mode, setMode] = React.useState<"signin" | "reset">("signin");
  const [remember, setRemember] = React.useState(true);

  React.useEffect(() => {
    setRemember(isRememberMe());
  }, []);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const signingInRef = React.useRef(false);

  /**
   * Webbläsarens lösenordshanterare fyller i fälten utan att skicka React-events,
   * så state kan vara tomt trots ifyllda fält. Läs därför alltid av DOM:en.
   */
  function readField(name: "email" | "password", fallback: string): string {
    // Lösenordsfältet saknar name-attribut med flit, så att ett formulär som
    // skickas innan sidan hunnit aktiveras aldrig kan lägga lösenordet i URL:en.
    const selector = name === "password" ? "input[type=password]" : "input[name=email]";
    const el = formRef.current?.querySelector(selector);
    const domValue = el instanceof HTMLInputElement ? el.value : "";
    return domValue || fallback;
  }

  // Fånga upp autofyll som skedde innan React hann montera fälten.
  React.useEffect(() => {
    const sync = () => {
      const nextEmail = readField("email", "");
      const nextPassword = readField("password", "");
      if (nextEmail) setEmail((prev) => prev || nextEmail);
      if (nextPassword) setPassword((prev) => prev || nextPassword);
    };
    sync();
    const timer = window.setTimeout(sync, 350);
    return () => window.clearTimeout(timer);
  }, [mode]);

  async function signIn(event: React.FormEvent) {
    event.preventDefault();
    if (signingInRef.current) return;
    const currentEmail = readField("email", email).trim();
    const currentPassword = readField("password", password);
    if (currentEmail !== email) setEmail(currentEmail);
    if (currentPassword !== password) setPassword(currentPassword);
    if (!currentEmail || !currentPassword) {
      reportFormError(t.missing, { toastId: "auth" });
      return;
    }
    signingInRef.current = true;
    setBusy(true);
    let result: Awaited<ReturnType<typeof signInGuarded>>;
    try {
      result = await signInGuarded({ data: { email: currentEmail, password: currentPassword } });
    } catch {
      signingInRef.current = false;
      setBusy(false);
      reportFormError(t.generic, { toastId: "auth" });
      return;
    }

    if (result.status === "blocked") {
      signingInRef.current = false;
      setBusy(false);
      reportFormError(t.blocked(formatWait(result.seconds, locale)), { toastId: "auth" });
      return;
    }
    if (result.status !== "ok" || !result.session) {
      signingInRef.current = false;
      setBusy(false);
      reportFormError(t.invalid, { toastId: "auth" });
      return;
    }

    setRememberMe(remember);
    const { error } = await supabase.auth.setSession({
      access_token: result.session.access_token,
      refresh_token: result.session.refresh_token,
    });
    if (error) {
      signingInRef.current = false;
      setBusy(false);
      reportFormError(t.generic, { toastId: "auth" });
      return;
    }
    const { data: verified, error: verificationError } = await supabase.auth.getUser();
    if (verificationError || !verified.user) {
      signingInRef.current = false;
      setBusy(false);
      reportFormError(t.generic, { toastId: "auth" });
      return;
    }
    await recordSignIn({ data: { method: "lösenord" } }).catch(() => {});
    await navigate({ to: destination as "/admin" });
    signingInRef.current = false;
    setBusy(false);
  }

  async function sendReset(event: React.FormEvent) {
    event.preventDefault();
    const currentEmail = readField("email", email).trim();
    if (currentEmail !== email) setEmail(currentEmail);
    if (!currentEmail) {
      reportFormError(t.missing, { toastId: "auth" });
      return;
    }
    setBusy(true);
    try {
      const res = await requestPasswordReset({
        data: { email: currentEmail, origin: window.location.origin, locale },
      });
      setBusy(false);
      if (res.status === "blocked") {
        reportFormError(t.blocked(formatWait(res.seconds, locale)), { toastId: "auth" });
        return;
      }
    } catch {
      setBusy(false);
      reportFormError(t.generic, { toastId: "auth" });
      return;
    }
    // Neutralt svar oavsett om kontot finns, så att konton inte kan kartläggas.
    toast.success(t.resetSent, { id: "auth" });
    setMode("signin");
  }

  async function sendMagicLink() {
    const currentEmail = readField("email", email).trim();
    if (currentEmail !== email) setEmail(currentEmail);
    if (!currentEmail) {
      reportFormError(t.missing, { toastId: "auth" });
      return;
    }
    setBusy(true);
    setRememberMe(remember);
    try {
      const res = await requestMagicLink({
        data: { email: currentEmail, origin: window.location.origin, locale },
      });
      if (res.status === "blocked") {
        reportFormError(t.blocked(formatWait(res.seconds, locale)), { toastId: "auth" });
      } else {
        toast.success(t.magicSent, { id: "auth" });
      }
    } catch {
      reportFormError(t.generic, { toastId: "auth" });
    }
    setBusy(false);
  }

  if (mode === "reset") {
    return (
      <Shell title={t.resetTitle} intro={t.resetIntro}>
        <form ref={formRef} noValidate onSubmit={sendReset} className="mt-6 space-y-4">
          <TextField
            label={t.email}
            type="email"
            inputMode="email"
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={setEmail}
            name="email"
            autoComplete="username"
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {busy ? t.resetSubmitting : t.resetSubmit}
          </button>
          <button
            type="button"
            onClick={() => setMode("signin")}
            className="w-full text-center text-sm text-muted-foreground link-quiet hover:text-primary"
          >
            {t.back}
          </button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title={t.title} intro={t.intro}>
      <form ref={formRef} noValidate onSubmit={signIn} className="mt-6 space-y-4">
        <TextField
          label={t.email}
          type="email"
          inputMode="email"
          placeholder={t.emailPlaceholder}
          value={email}
          onChange={setEmail}
          name="email"
          autoComplete="username"
          required
        />
        <PasswordField
          label={t.password}
          value={password}
          onChange={setPassword}
          placeholder={t.passwordPlaceholder}
          showLabel={t.show}
          hideLabel={t.hide}
          autoComplete="current-password"
        />
        <CheckRow checked={remember} onChange={setRemember} className="items-start">
          <span className="block">
            {t.remember}
            <span className="mt-1 block text-xs text-muted-foreground">{t.rememberHelp}</span>
          </span>
        </CheckRow>
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy ? t.submitting : t.submit}
        </button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          {t.magic}
          <span className="h-px flex-1 bg-border" />
        </div>
        <button
          type="button"
          onClick={() => void sendMagicLink()}
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
        >
          <Mail className="h-4 w-4" aria-hidden="true" />
          {busy ? t.magicSending : t.magicSubmit}
        </button>
        <button
          type="button"
          onClick={() => setMode("reset")}
          className="w-full text-center text-sm text-muted-foreground link-quiet hover:text-primary"
        >
          {t.forgot}
        </button>
      </form>
    </Shell>
  );
}

/** Sätt nytt lösenord efter att ha klickat på återställningslänken i mailet. */
export function ResetPasswordScreen({ locale }: { locale: Locale }) {
  const t = COPY[locale];
  const navigate = useNavigate();
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  // Länken i mejlet bär en engångskod. Den växlas in mot en session här,
  // och tas bort ur adressfältet så att den inte kan delas vidare.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const tokenHash = url.searchParams.get("token_hash");
    if (!tokenHash) return;
    url.searchParams.delete("token_hash");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    void supabase.auth
      .verifyOtp({ type: "recovery", token_hash: tokenHash })
      .then(({ error }) => {
        if (error) reportFormError(t.linkExpired, { toastId: "auth" });
      })
      .catch(() => reportFormError(t.linkExpired, { toastId: "auth" }));
  }, [t.linkExpired]);



  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < 8) {
      reportFormError(t.tooShort, { toastId: "auth" });
      return;
    }
    if (password !== repeat) {
      reportFormError(t.mismatch, { toastId: "auth" });
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      const expired = /session|token|expired|jwt/i.test(error.message);
      reportFormError(expired ? t.linkExpired : t.generic, { toastId: "auth" });
      return;
    }
    toast.success(t.saved, { id: "auth" });
    void navigate({ to: "/admin" });
  }

  return (
    <Shell title={t.newTitle} intro={t.newIntro}>
      <form noValidate onSubmit={save} className="mt-6 space-y-4">
        <PasswordField
          label={t.newPassword}
          value={password}
          onChange={setPassword}
          placeholder={t.newPasswordPlaceholder}
          showLabel={t.show}
          hideLabel={t.hide}
          autoComplete="new-password"
        />
        <PasswordField
          label={t.repeatPassword}
          value={repeat}
          onChange={setRepeat}
          placeholder={t.repeatPlaceholder}
          showLabel={t.show}
          hideLabel={t.hide}
          autoComplete="new-password"
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy ? t.saving : t.save}
        </button>
        <p className="text-center text-sm">
          <Link
            to={AUTH_PATH[locale] as never}
            className="text-muted-foreground link-quiet hover:text-primary"
          >
            {t.back}
          </Link>
        </p>
      </form>
    </Shell>
  );
}
